/**
 * AuiAgentTaskList
 *
 * Self-discovering surface component that renders a hierarchical task backlog
 * for a coordinator run. The agent emits { type: "AgentTaskList" } and the
 * frontend handles cold-load via REST, real-time updates via WebSocket,
 * and hierarchical rendering via parentTaskId nesting.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence } from 'motion/react';
import { AnimatedListItem } from '../../ui/AnimatedListItem';
import { AgentTaskItem } from './AgentTaskItem';
import { useAuiSurface } from '../AuiSurfaceContext';
import { usePlatformEvents } from '../../../contexts/PlatformEventContext';
import { spaceApi } from '../../../services/api';
import { useCanvases } from '../../../contexts/CanvasContext';
import type { AuiRendererProps } from '../registry';
import type { PlatformEvent } from '../../../types/events';
import type { AgentTask, AgentTaskStatus, AgentTaskEventData } from '../../../types/agent-tasks';

const STATUS_PRIORITY: Record<AgentTaskStatus, number> = {
  running: 0,
  pending: 1,
  blocked: 2,
  completed: 3,
  failed: 3,
};

function sortTasks(tasks: AgentTask[]): AgentTask[] {
  return [...tasks].sort((a, b) => {
    const pa = STATUS_PRIORITY[a.status] ?? 3;
    const pb = STATUS_PRIORITY[b.status] ?? 3;
    if (pa !== pb) return pa - pb;
    return a.createdAt.localeCompare(b.createdAt);
  });
}

export function AuiAgentTaskList(_props: AuiRendererProps) {
  const { runId } = useAuiSurface();
  const { spaceId } = useCanvases();
  const { subscribe, isConnected } = usePlatformEvents();

  const [tasks, setTasks] = useState<Map<string, AgentTask>>(new Map());
  const [loaded, setLoaded] = useState(false);

  // ── Cold-load ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!runId) return;
    let cancelled = false;

    (spaceId ? spaceApi(spaceId).runs.agentTasks(runId) : Promise.resolve([])).then((apiTasks) => {
      if (cancelled) return;
      const map = new Map<string, AgentTask>();
      for (const t of apiTasks) map.set(t.taskId, t);
      setTasks((prev) => {
        const merged = new Map(map);
        for (const [id, entry] of prev) {
          if (!merged.has(id)) merged.set(id, entry);
        }
        return merged;
      });
      setLoaded(true);
    }).catch(() => {
      setLoaded(true);
    });

    return () => { cancelled = true; };
  }, [runId]);

  // ── Re-fetch on reconnection ──────────────────────────────────────
  const wasConnectedRef = useRef(isConnected);
  useEffect(() => {
    const wasConnected = wasConnectedRef.current;
    wasConnectedRef.current = isConnected;

    if (!wasConnected && isConnected && runId) {
      (spaceId ? spaceApi(spaceId).runs.agentTasks(runId) : Promise.resolve([])).then((apiTasks) => {
        const map = new Map<string, AgentTask>();
        for (const t of apiTasks) map.set(t.taskId, t);
        setTasks((prev) => {
          const merged = new Map(map);
          for (const [id, entry] of prev) {
            if (!merged.has(id)) merged.set(id, entry);
          }
          return merged;
        });
      }).catch(() => { /* silent — existing data remains */ });
    }
  }, [isConnected, runId]);

  // ── WebSocket subscription ─────────────────────────────────────────
  useEffect(() => {
    if (!runId) return;

    const unsubscribe = subscribe(
      [{ eventType: 'agent.run.agent_task_*', runId }],
      (event: PlatformEvent) => {
        const data = event.data as unknown as AgentTaskEventData;
        if (!data.taskId) return;

        const eventSuffix = event.eventType.replace('agent.run.agent_task_', '');

        setTasks((prev) => {
          const next = new Map(prev);

          if (eventSuffix === 'deleted') {
            next.delete(data.taskId);
            return next;
          }

          if (eventSuffix === 'created' || eventSuffix === 'blocked') {
            if (!next.has(data.taskId)) {
              next.set(data.taskId, eventToTask(data));
            }
            return next;
          }

          // For updates, merge into existing task
          const existing = next.get(data.taskId);
          if (existing) {
            next.set(data.taskId, { ...existing, ...eventToPartial(data, eventSuffix) });
          } else {
            // Task not seen yet — create from event data
            next.set(data.taskId, eventToTask(data));
          }
          return next;
        });
      },
    );

    return unsubscribe;
  }, [subscribe, runId]);

  // ── Tree building ──────────────────────────────────────────────────
  const { rootTasks, childrenMap } = useMemo(() => {
    const cMap = new Map<string | undefined, AgentTask[]>();
    for (const task of tasks.values()) {
      const key = task.parentTaskId ?? undefined;
      const list = cMap.get(key);
      if (list) list.push(task);
      else cMap.set(key, [task]);
    }
    // Sort each group
    for (const [key, group] of cMap) {
      cMap.set(key, sortTasks(group));
    }
    return { rootTasks: cMap.get(undefined) ?? [], childrenMap: cMap };
  }, [tasks]);

  // ── Render ─────────────────────────────────────────────────────────
  if (!loaded && tasks.size === 0) {
    return (
      <div className="flex flex-col gap-2" data-testid="aui-agent-task-list">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-8 rounded bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (tasks.size === 0) {
    return (
      <div data-testid="aui-agent-task-list" className="text-sm text-muted-foreground py-4 text-center">
        No tasks yet
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5" data-testid="aui-agent-task-list">
      <AnimatePresence initial={false}>
        {rootTasks.map((task) => (
          <AnimatedListItem key={task.taskId}>
            <AgentTaskItem
              task={task}
              allTasks={tasks}
              childrenMap={childrenMap}
              depth={0}
            />
          </AnimatedListItem>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

function eventToTask(data: AgentTaskEventData): AgentTask {
  return {
    taskId: data.taskId,
    rootRunId: data.runId,
    runId: data.runId,
    parentTaskId: data.parentTaskId,
    agentId: data.agentId,
    agentName: data.agentName,
    title: data.title,
    activeForm: data.activeForm,
    status: data.status,
    orgId: '',
    blockedBy: data.blockedBy,
    linkedSubagentRunId: data.linkedSubagentRunId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    durationMs: data.durationMs,
    error: data.error,
  };
}

function eventToPartial(data: AgentTaskEventData, eventSuffix: string): Partial<AgentTask> {
  const base: Partial<AgentTask> = {
    status: data.status,
    updatedAt: new Date().toISOString(),
  };
  if (data.title) base.title = data.title;
  if (data.activeForm !== undefined) base.activeForm = data.activeForm;

  switch (eventSuffix) {
    case 'started':
      base.activeForm = data.activeForm;
      break;
    case 'completed':
      base.durationMs = data.durationMs;
      base.completedAt = new Date().toISOString();
      break;
    case 'failed':
      base.error = data.error;
      base.durationMs = data.durationMs;
      break;
    case 'unblocked':
      base.blockedBy = [];
      break;
  }

  return base;
}
